import type { RawApiResponse } from "@/lib/apiClient";
import type { ReaderRequest, ReaderValues } from "./types";

const searchKeys = [
  "id", "tripId", "eventId", "costId", "bagId", "token", "publicShareId",
  "q", "lang", "fields", "countryCode", "from", "to", "amount",
  "connectionId", "parentId", "cursor", "limit", "day", "mode",
  "isTicket", "latitude", "longitude", "locations", "type", "tab", "types", "region",
];

export function readerSearch(search: Record<string, unknown>): ReaderValues {
  return Object.fromEntries(searchKeys.flatMap((key) => {
    const value = search[key];
    if (key === "isTicket" && typeof value === "boolean") return [[key, String(value)]];
    return typeof value === "string" ? [[key, value]] : [];
  }));
}

/** Encode each identifier separately so it can never change the endpoint path. */
export function resolveReaderPath(template: string, values: ReaderValues): string | null {
  let complete = true;
  const path = template.replace(/:([A-Za-z][A-Za-z0-9]*)/g, (_, key: string) => {
    const value = values[key];
    if (!value) complete = false;
    return encodeURIComponent(value ?? "");
  });
  return complete ? path : null;
}

export function readerRequestUrl(request: ReaderRequest, values: ReaderValues): string | null {
  const path = resolveReaderPath(request.path, values);
  if (!path) return null;
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(request.query ?? {})) {
    const resolved = value.startsWith(":") ? values[value.slice(1)] : value;
    if (resolved === undefined) return null;
    query.set(key, resolved);
  }
  for (const key of request.search ?? []) {
    const value = values[key];
    if (value !== undefined && value !== "") query.set(key, value);
  }
  return `${path}${query.size ? `?${query}` : ""}`;
}

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : null;
}

export function deriveReaderValues(
  request: ReaderRequest,
  values: ReaderValues,
  responses: (RawApiResponse | undefined)[],
): ReaderValues {
  if (request.derive === "country-images") {
    for (const response of responses) {
      const country = response?.status === 200 ? record(response.body) : null;
      if (typeof country?.cc2 === "string") return { ...values, countryCode: country.cc2 };
    }
  }
  if (request.derive === "cost") {
    for (const response of responses) {
      const cost = response?.status === 200 ? record(response.body) : null;
      if (cost?.id === values.costId && typeof cost?.eventId === "string")
        return { ...values, eventId: cost.eventId };
    }
  }
  return values;
}

/** Small navigation lists accompany the complete, unchanged JSON below them. */
export function readerRecordLinks(path: string, body: unknown, values: ReaderValues) {
  const container = record(body);
  let rows: unknown = body;
  let target: string | null = null;
  let key = "id";
  if (path === "/v1/trips") target = "/trips/:id";
  else if (path === "/v1/countries") target = "/country/:id";
  else if (path === "/v1/packing-lists") target = "/packing-lists/:id";
  else if (path === "/v1/trips/:id/events") {
    rows = container?.items;
    target = "/trips/:id/events/:eventId";
    key = "eventId";
  } else if (path === "/v1/trips/:id/costs") {
    rows = container?.items;
    target = "/trips/:id/costs/:costId";
    key = "costId";
  } else if (path === "/v1/packing-lists/:id") {
    rows = container?.bags;
    target = "/packing-lists/:id/bags/:bagId/edit";
    key = "bagId";
  }
  if (!target || !Array.isArray(rows)) return [];
  const route = target;
  return rows.flatMap((row) => {
    const item = record(row);
    if (typeof item?.id !== "string") return [];
    const href = resolveReaderPath(route, { ...values, [key]: item.id });
    if (!href) return [];
    const label = item.name ?? item.description ?? item.id;
    return [{ href, label: typeof label === "string" ? label : item.id }];
  });
}
